import { TranslateService } from '@ngx-translate/core';
import { CustomDateFilterUtils, CustomRendererComponent } from '../ag-grid';
import { Directive, TemplateRef } from '@angular/core';

import {
  BodyScrollEvent,
  CellClickedEvent,
  ColDef,
  Column,
  ColumnApi,
  ColumnResizedEvent,
  GridApi,
  GridOptions,
  ProcessCellForExportParams,
  RowClickedEvent,
  StatusPanelDef,
} from 'ag-grid-community';
import {
  AG_GRID_LOCALE_EN,
  AG_GRID_LOCALE_IT,
  AvailableLanguages,
  BooleanEnum,
  ColAction,
  ColActionId,
  EN_DATE_FORMAT_CLASIC,
  IT_DATE_FORMAT_CLASSIC,
  ListData,
  PtDomainsCategory,
  PtEntity,
} from '@pt/pt-models';
import { TableInputDirective } from '@pt/directives';
import { ColumnWithLocalization } from 'it/primatoo/openapi/api/org/schemas/common/ColumnWithLocalization';
import { DatePipe } from '@angular/common';
import { Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { ag_grid_frameworkComponents } from '../pt-shared.constant';
import {
  PermissionManager,
  PRIMATOO_BASE_PROD_ENVIRONMENT,
  TimeUtils,
} from '@pt/pt-core';
import { AppActionsBuilder } from './app-actions-builder/app-actions-builder';
import { ColumnParams } from 'it/primatoo/openapi/api/org/schemas/common/ColumnParams';
import { CustomLoadingOverlayComponent } from '@pt/loader';
import { CustomHeaderTooltipComponent } from '../ag-grid/custom-header-tooltip/custom-header-tooltip.component';

// Add this col key to prevent be col-config named with "actions"
const AG_GRID_ACTIONS_COL = 'agGridActionsCol';

@Directive()
export abstract class AgGridTableWrapper<E> extends TableInputDirective<E> {
  public gridApi: GridApi;
  public columnApi: ColumnApi;
  public scrollTableSubject$: Subject<BodyScrollEvent> =
    new Subject<BodyScrollEvent>();
  public moveColumnTableSubject$: Subject<ColumnParams[]> = new Subject<
    ColumnParams[]
  >();
  public userColumnsVisibleSubject$: Subject<ColumnParams[]> = new Subject<
    ColumnParams[]
  >();
  private _colDefs: ColDef[];
  private _gridOptions: GridOptions;
  private _data: ListData<E>;
  private _selectedData: E[] = [];
  private entityWithCache: PtEntity[] = ENTITY_WITH_CACHE;

  public loadingOverlayComponent: any = CustomLoadingOverlayComponent;
  public loadingOverlayComponentParams: any = {
    loadingMessage: 'Ricaricare la tabella',
  };
  public statusBar: {
    statusPanels: StatusPanelDef[];
  } = {
    statusPanels: [
      { statusPanel: 'agTotalAndFilteredRowCountComponent' },
      { statusPanel: 'agTotalRowCountComponent' },
      { statusPanel: 'agFilteredRowCountComponent' },
      { statusPanel: 'agSelectedRowCountComponent' },
      { statusPanel: 'agAggregationComponent' },
    ],
  };

  private _defaultColDef: ColDef = {
    filter: false,
    floatingFilter: false,
    autoHeaderHeight: true,
  };
  private localColumn: ColumnParams[];
  private resetColumnsLabel = '';
  private readonly localeText = AG_GRID_LOCALE_EN;
  private _actionsBuilder: AppActionsBuilder;
  private cellClickSubject$: Subject<{ action: ColAction; row: E }> =
    new Subject<{ action: ColAction; row: E }>();
  private filterEntity: PtEntity;
  private doubleClick = false;

  protected constructor(
    public translate: TranslateService,
    public permissionManager: PermissionManager
  ) {
    super();

    this.localeText =
      translate.currentLang == 'it' ? AG_GRID_LOCALE_IT : AG_GRID_LOCALE_EN;
    translate
      .get('BUTTON.RESETCOL')
      .subscribe((label) => (this.resetColumnsLabel = label));
    this._actionsBuilder = new AppActionsBuilder(translate, permissionManager);

    this.cellClickSubject$.pipe(debounceTime(250)).subscribe((action) => {
      this.clickActions({ id: action.action.id }, action.row);
    });

    this.scrollTableSubject$.pipe(debounceTime(1500)).subscribe((action) => {
      action.api.hideOverlay();
    });

    this.moveColumnTableSubject$.pipe(debounceTime(250)).subscribe((action) => {
      this.userColumnsVisible.emit({ columns: action });
    });

    this.userColumnsVisibleSubject$
      .pipe(debounceTime(250))
      .subscribe((action) => {
        this.userColumnsVisible.emit({ columns: action });
      });
  }

  public get currentSelectedRows(): E[] {
    if (this._selectedData === undefined) {
      return [];
    }
    return this._selectedData;
  }

  /**
   * @param columnParams
   * @protected
   */
  public static generateColDef(columnParams: ColumnWithLocalization): ColDef {
    switch (columnParams?.type) {
      case 'FILE_SIZE':
        return {
          // headerTooltip: columnParams.help,
          headerComponent: CustomHeaderTooltipComponent,
          headerComponentParams: {
            field: columnParams.column,
            tooltip: columnParams.help,
          },
          field: columnParams.column,
          headerName: columnParams.label,
          flex: columnParams.size ? 0 : 1,
          width: columnParams.size,
          resizable: true,
          minWidth: 100,
          cellRenderer: 'fileSizeRenderer',
          filter: 'agNumberColumnFilter',
          floatingFilter: true,
          sortable: true,
          autoHeaderHeight: true,
          hide: !columnParams.uiUserShow,
          suppressColumnsToolPanel: !columnParams.uiAvailable,
          suppressNavigable: !columnParams.uiAvailable,
        } as ColDef;
      case 'PROGRESS':
        return {
          // headerTooltip: columnParams.help,
          headerComponent: CustomHeaderTooltipComponent,
          headerComponentParams: {
            field: columnParams.column,
            tooltip: columnParams.help,
          },
          field: columnParams.column,
          headerName: columnParams.label,
          flex: columnParams.size ? 0 : 1,
          width: columnParams.size,
          resizable: true,
          minWidth: 100,
          cellRenderer: 'progressRenderer',
          filter: 'agNumberColumnFilter',
          floatingFilter: true,
          sortable: true,
          autoHeaderHeight: true,
          hide: !columnParams.uiUserShow,
          suppressColumnsToolPanel: !columnParams.uiAvailable,
          suppressNavigable: !columnParams.uiAvailable,
        } as ColDef;
      case 'BOOLEAN':
        return {
          headerName: columnParams.label,
          headerComponent: CustomHeaderTooltipComponent,
          headerComponentParams: {
            field: columnParams.column,
            tooltip: columnParams.help,
          },
          field: columnParams.column,
          flex: columnParams.size ? 0 : 1,
          resizable: true,
          width: columnParams.size,
          minWidth: 100,
          filter: 'agSetColumnFilter',
          floatingFilter: true,
          floatingFilterComponent: 'booleanToAdverbFilterComponent',
          sortable: true,

          cellRenderer: 'booleanCellRenderer',
          autoHeaderHeight: true,
          filterParams: {
            values: [BooleanEnum.FALSE, BooleanEnum.TRUE],
            cellRenderer: 'booleanToAdverbsRendererComponent',
            suppressMiniFilter: true,
          },
          hide: !columnParams.uiUserShow,
          suppressColumnsToolPanel: !columnParams.uiAvailable,
          suppressNavigable: !columnParams.uiAvailable,
        } as ColDef;
      case 'DATE':
        return {
          headerName: columnParams.label,
          headerComponent: CustomHeaderTooltipComponent,
          headerComponentParams: {
            field: columnParams.column,
            tooltip: columnParams.help,
          },
          field: columnParams.column,
          minWidth: 140,
          suppressMenu: true,
          width: columnParams.size,
          cellRenderer: 'dateFormatterRenderer',
          resizable: true,
          sortable: true,
          autoHeaderHeight: true,
          ...CustomDateFilterUtils.customDataFilterColDefinition(),
          hide: !columnParams.uiUserShow,
          suppressColumnsToolPanel: !columnParams.uiAvailable,
          suppressNavigable: !columnParams.uiAvailable,
        } as ColDef;
      case 'NUMBER':
        return {
          headerName: columnParams.label,
          headerComponent: CustomHeaderTooltipComponent,
          headerComponentParams: {
            field: columnParams.column,
            tooltip: columnParams.help,
          },
          field: columnParams.column,
          flex: columnParams.size ? 0 : 1,
          width: columnParams.size,
          minWidth: 100,
          resizable: true,
          filter: 'agNumberColumnFilter',
          floatingFilter: true,
          autoHeaderHeight: true,
          sortable: true,
          hide: !columnParams.uiUserShow,
          suppressColumnsToolPanel: !columnParams.uiAvailable,
          suppressNavigable: !columnParams.uiAvailable,
        } as ColDef;
      case 'DATE_TEXT':
        return {
          headerName: columnParams.label,
          headerComponent: CustomHeaderTooltipComponent,
          headerComponentParams: {
            field: columnParams.column,
            tooltip: columnParams.help,
          },
          field: columnParams.column,
          flex: columnParams.size ? 0 : 1,
          width: columnParams.size,
          comparator: (valueA, valueB) => {
            if (valueA == valueB) return 0;
            if (!valueA) return -1;
            if (!valueB) return 1;
            const t1 = TimeUtils.transformDDMMYYYYtoYYYYMMDD(valueA);
            const t2 = TimeUtils.transformDDMMYYYYtoYYYYMMDD(valueB);
            return t1 > t2 ? 1 : -1;
          },
          minWidth: 100,
          resizable: true,
          tooltipField: columnParams?.column,
          tooltipComponentParams: { field: columnParams.column },
          tooltipComponent: 'customTooltipComponent',
          filter: 'agTextColumnFilter',
          floatingFilter: true,
          filterParams: {
            buttons: ['reset'],
            closeOnApply: true,
          },
          autoHeaderHeight: true,
          sortable: true,
          hide: !columnParams.uiUserShow,
          suppressColumnsToolPanel: !columnParams.uiAvailable,
          suppressNavigable: !columnParams.uiAvailable,
        } as ColDef;
      case 'TEXT':
      default:
        return {
          headerName: columnParams.label,
          headerComponent: CustomHeaderTooltipComponent,
          headerComponentParams: {
            field: columnParams.column,
            tooltip: columnParams.help,
          },
          autoHeaderHeight: true,
          field: columnParams.column,
          flex: columnParams.size ? 0 : 1,
          width: columnParams.size,
          minWidth: 100,
          resizable: true,
          tooltipField: columnParams?.column,
          tooltipComponentParams: { field: columnParams.column },
          tooltipComponent: 'customTooltipComponent',
          filter: 'agTextColumnFilter',
          floatingFilter: true,
          filterParams: {
            buttons: ['reset'],
            closeOnApply: true,
          },
          sortable: true,
          hide: !columnParams.uiUserShow,
          suppressColumnsToolPanel: !columnParams.uiAvailable,
          suppressNavigable: !columnParams.uiAvailable,
        } as ColDef;
    }
  }

  onChangeData(listData: ListData<E | any> | null): void {
    if (
      listData &&
      listData.data &&
      (listData.colEnabled || listData.customColDefinition !== undefined)
    ) {
      this._data = JSON.parse(JSON.stringify(listData));
      this.configureColumn(this._data);
    }

    if (this.gridApi) {
      const rowDataLength = listData?.data?.length || 0;
      this.gridApi.setPinnedBottomRowData([rowDataLength]);
    }

    /**Controllo la presenza delle label*/
    if (listData && listData.colEnabled) {
      listData.colEnabled.forEach((col: any) => {
        if (!col.label) {
          console.warn(`Column "${col.column}" is missing a label `);
        }
      });
    }
    this.setLoading(false);
  }

  gridOptions(): GridOptions {
    return this._gridOptions;
  }

  cellClicked(cell: CellClickedEvent) {
    this.doubleClick = false;
    setTimeout(() => {
      if (!this.doubleClick) {
        // Ignoro il click della colonna ACTION
        if (cell.column.getId() !== AG_GRID_ACTIONS_COL) {
          this.cellClickSubject$.next({
            action: { id: ColActionId.showDetail },
            row: cell.data,
          });
        }
      }
    }, 250);
  }

  cellDoubleClicked(row: CellClickedEvent, entity: PtEntity) {
    this.doubleClick = true;
    if (row.column.getId() !== AG_GRID_ACTIONS_COL) {
      this.clickActions({ id: ColActionId.open }, row.data);
    }
  }

  rowSelected(row: RowClickedEvent, api: GridApi) {
    if (api) {
      const selectedData: E[] = api.getSelectedRows();
      this._selectedData = selectedData;
      this.selectData.emit(selectedData);
    }
  }

  setLoading(value: boolean) {
    if (this.gridApi) {
      if (value) {
        this.gridApi.showLoadingOverlay();
      } else {
        this.gridApi.hideOverlay();
      }
    }
  }

  onChangeColumnsVisible(columns: Column[]) {
    this.setLoadingIfRequired(columns);

    const _userColumnsVisible: ColumnParams[] = [];
    if (!this.localColumn) {
      this.localColumn = JSON.parse(JSON.stringify(this.data.colEnabled));
    }
    const currentAgGridColChanged = columns?.map((c) => {
      return { columns: c.getColId(), enabled: c.isVisible() };
    });

    if (this.localColumn?.length > 0 && currentAgGridColChanged?.length > 0) {
      let currentNewVisibleColumn: ColumnParams;
      this.localColumn.forEach((col: ColumnParams) => {
        const changedColumn = currentAgGridColChanged?.find(
          (c) => c.columns === col.column
        );
        if (changedColumn) {
          currentNewVisibleColumn = {
            column: col.column,
            type: col.type,
            size: col.size,
            // enabled: changedColumn.enabled,
            uiUserShow: changedColumn.enabled,
            uiAvailable: col.uiAvailable,
            operations: col.operations,
          } as ColumnParams;
        } else {
          _userColumnsVisible.push({
            column: col.column,
            type: col.type,
            size: col.size,
            uiUserShow: col.uiUserShow,
            uiAvailable: col.uiAvailable,
            operations: col.operations,
          } as ColumnParams);
        }
      });
      // la colonna diventata visibile viene aggiunta in fondo
      if (currentNewVisibleColumn) {
        _userColumnsVisible.push(currentNewVisibleColumn);
      }
    }

    this.localColumn = _userColumnsVisible;
    const visibleColumn = this.localColumn.filter((col) => col.uiUserShow);
    if (visibleColumn.length >= 1 && this.gridApi) {
      const currentColDef: ColDef[] = [];
      if (visibleColumn.length === 1) {
        this.gridApi.getColumnDefs().forEach((colDef: ColDef) => {
          if (colDef.field === visibleColumn[0].column) {
            currentColDef.push({
              ...colDef,
              lockVisible: true,
              cellClass: 'locked-visible',
            });
          } else {
            currentColDef.push({ ...colDef });
          }
        });
        this.gridApi.setColumnDefs(currentColDef);
        // this.applyExternalState();
      } else {
        this.gridApi.getColumnDefs().forEach((colDef: ColDef) => {
          if (colDef.headerName !== '') {
            currentColDef.push({
              ...colDef,
              lockVisible: false,
              cellClass: '',
            });
          } else {
            currentColDef.push(colDef);
          }
        });
        this.gridApi.setColumnDefs(currentColDef);
        // this.applyExternalState();
      }
      this.userColumnsVisibleSubject$.next(this.localColumn);
    }
  }

  protected applyExternalState(): void {
    if (!this.gridApi) return;

    if (this.externalFilterModel) {
      this.gridApi.setFilterModel(this.externalFilterModel);
    }

    if (this.externalSortModel) {
      this.columnApi.applyColumnState({
        state: this.externalSortModel,
        defaultState: { sort: null },
      });
    }
  }

  onMoveColumns(columnMoveEvent) {
    if (!this.localColumn) {
      this.localColumn = JSON.parse(JSON.stringify(this.data.colEnabled));
    }
    if (columnMoveEvent) {
      // se è presente la colonna delle checkbox l'indice della colonna deve essere ridotto di 1
      const columnIndex = this.useCheckboxColumn
        ? columnMoveEvent?.toIndex - 1
        : columnMoveEvent?.toIndex;
      const columnId = columnMoveEvent?.column?.colId;
      const currentCol = this.localColumn?.find((c) => c.column === columnId);
      if (currentCol) {
        const newList = [];
        let visibleIndex = 0;
        if (columnIndex === visibleIndex) {
          newList.push(currentCol);
          visibleIndex++;
        }
        // 1. scandisco tutte le colonne
        this.localColumn.forEach((col) => {
          // 2. è la colonna corrente la scarto e la inserisco nella sua posizione
          if (col.column !== currentCol.column) {
            if (col.uiUserShow) {
              // 4. se sono nella posizione dove deve andare la colonna corrente la inserisco e incremento il contatore
              if (columnIndex === visibleIndex) {
                newList.push(currentCol);
                visibleIndex++;
              }
              // 3. ho trovato una colonna visibile ed incremento il contatore
              visibleIndex++;
            }
            // 5. aggiungo alla lista tutte le colonne a meno di quella corrente che viene inserita al punto 4.
            newList.push(col);
          }
        });
        // sono nell'ultima posizione
        if (newList.length !== this.localColumn.length) {
          newList.push(currentCol);
        }

        this.localColumn = newList;
      }
    }

    // utilizzo un subject per prendere il valore più aggiornato
    // evito di fare tanti emit quanti gli spostamenti fatti
    this.moveColumnTableSubject$.next(this.localColumn);
  }

  onResizeColumn($event: ColumnResizedEvent) {
    const _userColumnsResized: ColumnParams[] = [];

    if (!this.localColumn) {
      this.localColumn = JSON.parse(JSON.stringify(this.data.colEnabled));
    }

    if ($event.finished) {
      const currentAgGridColChanged = $event?.columns.map((c) => {
        return { columns: c.getColId(), size: c.getActualWidth() };
      });

      if (this.localColumn?.length > 0 && currentAgGridColChanged?.length > 0) {
        this.localColumn.forEach((col: ColumnParams) => {
          const changedColumn = currentAgGridColChanged.find(
            (c) => c.columns === col.column
          );
          col.size = changedColumn ? changedColumn.size : col.size;
          _userColumnsResized.push(col as ColumnParams);
        });
      }

      this.localColumn = _userColumnsResized;
      this.userColumnsVisibleSubject$.next(this.localColumn);
    }
  }

  exportTable(prefix: string, pattern = 'short') {
    const lang = this.translate.currentLang;
    const datePipe: DatePipe = new DatePipe(lang);
    let dateFormat: string;

    switch (lang) {
      case AvailableLanguages.en:
        dateFormat = EN_DATE_FORMAT_CLASIC;
        break;
      case AvailableLanguages.it:
        dateFormat = IT_DATE_FORMAT_CLASSIC;
        break;
      default:
        dateFormat = EN_DATE_FORMAT_CLASIC;
        break;
    }
    if (!this.gridApi) {
      return;
    }
    this.gridApi.exportDataAsExcel({
      fileName: this.generateExportFileName(prefix, pattern),
      sheetName: this.translate.instant('LABELS.DATA'),
      columnKeys: this.gridApi
        .getColumnDefs()
        ?.filter((col: ColDef) => col.field !== 'checkbox' && !col.hide)
        ?.map((col: ColDef) => col.field),
      processCellCallback(params: ProcessCellForExportParams): string {
        if (
          params.column.getColDef().cellRenderer === 'dateFormatterRenderer' &&
          params.value !== undefined &&
          params.value !== null
        ) {
          return datePipe.transform(params.value, dateFormat);
        }
        return params.value;
      },
    });
  }

  public setUsableCheckboxColumnTo(columnApi: ColumnApi, status: boolean) {
    if (columnApi) {
      switch (status) {
        case true:
          columnApi.setColumnsVisible(['checkbox'], true);
          break;
        case false:
          columnApi.setColumnsVisible(['checkbox'], false);
          break;
      }
    }
  }

  onFirstDataRenderer(entity: PtEntity) {
    this.filterEntity = entity;
    if (!this.entityWithCache.includes(this.filterEntity)) {
      // empty
    } else {
      const filterModel = localStorage.getItem(
        `tb_${this.filterEntity}_filter_model`
      );
      if (filterModel) {
        this.gridApi.setFilterModel(JSON.parse(filterModel));
      }

      const sortModel = localStorage.getItem(
        `tb_${this.filterEntity}_sort_state`
      );
      if (sortModel) {
        const state = JSON.parse(sortModel);
        this.columnApi.applyColumnState({
          state: state,
          defaultState: { sort: null },
        });
      }
    }
  }

  onFilterChanged() {
    if (this.gridApi) {
      const filterModel = this.gridApi.getFilterModel();
      const sortState = this.columnApi.getColumnState();
      this.filterChange.emit(filterModel);
      this.sortChange.emit(sortState);

      this.gridApi.setPinnedBottomRowData([
        this.gridApi.getRenderedNodes().length,
      ]);
      if (
        this.filterEntity &&
        this.entityWithCache.includes(this.filterEntity)
      ) {
        const filterModel = this.gridApi.getFilterModel();
        localStorage.setItem(
          `tb_${this.filterEntity}_filter_model`,
          JSON.stringify(filterModel)
        );
        const colState = this.columnApi.getColumnState();
        const sortState = colState
          .filter(function (s) {
            return s.sort != null;
          })
          .map(function (s) {
            return { colId: s.colId, sort: s.sort, sortIndex: s.sortIndex };
          });
        localStorage.setItem(
          `tb_${this.filterEntity}_sort_state`,
          JSON.stringify(sortState)
        );
      }
    }
  }

  abstract createGridOptions(columnDefs: ColDef[], rowData: E[]): GridOptions;

  abstract clickActions(action: ColAction, row: E): void;

  abstract createColumnDefs(): ColDef[];

  abstract colActionsTemplate(): TemplateRef<unknown>;

  public reloadTable() {}

  public activeReloadTableOnChangeVisibleColumns(): boolean {
    return false;
  }

  protected defaultGridOptions(
    category: PtDomainsCategory,
    columnDefs: ColDef[],
    rowData: any[]
  ) {
    const baseOptions: GridOptions = {
      ...PRIMATOO_BASE_PROD_ENVIRONMENT.defaultGridOptions,
      rowData,
      columnDefs,

      localeText: this.localeText,
      suppressPropertyNamesCheck: true,
      components: ag_grid_frameworkComponents,
      context: {
        reloadTable: () => {
          this.reloadTable();
        },
      },
      getMainMenuItems: (params) => {
        const t = params.defaultItems.filter((c) => c !== 'resetColumns');
        return [
          ...t,
          {
            name:
              params?.context?.labels && params?.context?.labels['resetColumns']
                ? params?.context?.labels['resetColumns']
                : this.resetColumnsLabel,
            action: () => {
              this.resetColumns.emit(true);
            },
          },
        ] as any;
      },
      rowBuffer: 5,
      // pinnedBottomRowData: [rowData.length],
      // fullWidthCellRenderer: 'footerRenderComponent',
      // isFullWidthRow: (params) => params.rowNode.rowPinned === 'bottom',
      getContextMenuItems: () =>
        PRIMATOO_BASE_PROD_ENVIRONMENT.defaultContextMenuItems,
    } as GridOptions;

    switch (category) {
      case 'BPM': {
        return {
          ...baseOptions,
          enableRowGroup: true,
          debounceVerticalScrollbar: true,
        };
      }
      case 'ADM':
      case 'DPG': {
        return {
          ...baseOptions,
          debounceVerticalScrollbar: true,
        };
      }
      case 'WORKSPACE':
        return {
          ...baseOptions,
          debounceVerticalScrollbar: true,
        };
      default: {
        return {
          ...baseOptions,
        };
      }
    }
  }

  private generateExportFileName(prefix: string, pattern = 'short'): string {
    const datePipe: DatePipe = new DatePipe(this.translate.currentLang);
    return prefix + '(' + datePipe.transform(Date.now(), pattern) + ')';
  }

  private orderColDefField(
    unsortedColDef: ColDef[],
    colEnabled: ColumnWithLocalization[]
  ): ColDef[] {
    const hiddenCol = unsortedColDef?.filter((c: ColDef) => c.hide);
    const visibleCol = unsortedColDef?.filter((c: ColDef) => !c.hide);
    const orderedList = [];
    colEnabled.forEach((col: ColumnWithLocalization) => {
      const currentCol = visibleCol?.find((c) => c.field === col.column);
      if (currentCol) {
        orderedList.push(currentCol);
      }
    });
    orderedList.push(...hiddenCol);
    return orderedList;
  }

  private hideColDisabledAndOrderIt(
    uiColDefs: ColDef[],
    bkColDefs: ColumnWithLocalization[]
  ): ColDef[] {
    const response: ColDef[] = [];
    if (uiColDefs && bkColDefs) {
      // le colonne date dal bk sono quelle totali e le uniche da considerare
      // il tipo, se l'utente (in uiColDef) le ha personalizzate, vengono sovrascritte con quelle dell'utente
      const usedUiColField = [];
      bkColDefs.forEach((bkCol: ColumnWithLocalization) => {
        const currentUiColDef: ColDef = uiColDefs?.find(
          (cl: ColDef) => cl.field === bkCol.column
        );
        if (currentUiColDef) {
          usedUiColField.push(currentUiColDef.field);
          // l'utente ha personalizzato la colonna
          currentUiColDef.hide = !bkCol.uiUserShow;
          currentUiColDef.suppressNavigable = !bkCol.uiAvailable;
          currentUiColDef.minWidth = 100; // AGGIUNTO PER EVITARE COLONNE TROPPO PICCOLE
          currentUiColDef.headerName = bkCol.label;
          currentUiColDef.width = bkCol.size;
          currentUiColDef.flex = bkCol.size ? 0 : 1;
          response.push(currentUiColDef);
        } else {
          // colonna non personalizzata
          response.push(AgGridTableWrapper.generateColDef(bkCol));
        }
      });
      const enabledLockCol =
        response?.filter((c: ColDef) => !c.hide)?.length === 1;
      // se trova solo una colonna non nascosta blocca la possibilità di nascondere quest'ultima
      if (enabledLockCol) {
        const colToBlockHide = response?.find((c: ColDef) => !c.hide);
        const indexOfColToBlockHide = response?.indexOf(colToBlockHide);
        response.forEach((currentUiColDef: ColDef, index) => {
          if (index === indexOfColToBlockHide) {
            currentUiColDef.lockVisible = true;
            currentUiColDef.cellClass = 'locked-visible';
          } else {
            currentUiColDef.lockVisible = false;
            currentUiColDef.cellClass = '';
          }
        });
      } else {
        response.forEach((currentUiColDef: ColDef) => {
          currentUiColDef.lockVisible = false;
          currentUiColDef.cellClass = '';
        });
      }
      return this.orderColDefField(response, this._data?.colEnabled);
    }
    return response;
  }

  private createColActionDefIfRequired(): ColDef | null {
    if (this.colActionsTemplate()) {
      return {
        headerName: '',
        field: AG_GRID_ACTIONS_COL,
        flex: 1,
        width: 70,
        minWidth: 70,
        pinned: 'right',
        // maxWidth: 70,
        suppressNavigable: true,
        lockVisible: true,
        resizable: false,
        editable: false,
        suppressMovable: true,
        cellClass: 'no-border',
        cellRenderer: CustomRendererComponent,
        // pinned: 'right',
        cellRendererParams: {
          ngTemplate: this.colActionsTemplate(),
        },
      };
    }
    return null;
  }

  private createColCheckIfRequired(): ColDef | null {
    if (this.useCheckboxColumn) {
      return {
        headerName: '',
        field: 'checkbox',
        width: 50,
        minWidth: 50,
        suppressNavigable: true,
        lockVisible: true,
        lockPosition: true,
        editable: false,
        headerCheckboxSelection: true,
        headerCheckboxSelectionFilteredOnly: true,
        checkboxSelection: true,
      };
    }
    return null;
  }

  private configureColumn(listData: ListData<E>) {
    let colDefs;
    this._colDefs = undefined;

    // se customColDefinition è true prendo le colonne che la ui ha configurato altrimenti uso quelle del bk
    // è utilizzato SOLO per le tabelle di riepilogo presenti solitamente nelle pagine di dettaglio dove le colonne sono scelte
    // dal front-end
    if (listData.customColDefinition) {
      colDefs = this.createColumnDefs();
    } else {
      colDefs = this.hideColDisabledAndOrderIt(
        this.createColumnDefs(),
        JSON.parse(JSON.stringify(listData.colEnabled))
      );
    }

    const colActionDefs = this.createColActionDefIfRequired();
    if (colActionDefs) {
      colDefs.push(colActionDefs);
    }

    const colCheckboxDef = this.createColCheckIfRequired();
    if (colCheckboxDef) {
      colDefs.push(colCheckboxDef);
    }

    this._colDefs = colDefs;
    this._gridOptions = this.createGridOptions(this._colDefs, listData.data);
    if (!this._gridOptions.defaultColDef) {
      this._gridOptions.defaultColDef = this._defaultColDef;
    }
  }

  private setLoadingIfRequired(columns: Column[]) {
    if (this.activeReloadTableOnChangeVisibleColumns()) {
      const listColumnsChangedAndVisible: string[] = columns
        .filter((c) => c.isVisible())
        .map((c) => c.getColDef().field);
      if (listColumnsChangedAndVisible.length > 0) {
        // Verificare se sono già colonne visibile altrimenti fare il reload
        let activeLoading = false;
        this.data.colEnabled.forEach((c) => {
          if (
            listColumnsChangedAndVisible.includes(c.column) &&
            (!c.uiUserShow || !c.uiAvailable)
          ) {
            activeLoading = true;
          }
        });
        this.setLoading(activeLoading);
      }
    }
  }

  // selectDefaultRow() {
  //   if (
  //     this.defaultRowId !== null &&
  //     this.defaultRowId !== undefined &&
  //     !!this.gridOptions()
  //   ) {
  //     this.gridOptions().api.forEachNode((node) => {
  //       if (node.data.id === this.defaultRowId) {
  //         node.setSelected(true, true);
  //         this.gridOptions().api.ensureIndexVisible(node.rowIndex);
  //       }
  //     });
  //   }
  // }

  selectDefaultRow() {
    const api = this.gridApi;
    if (this.defaultRowId === null || this.defaultRowId === undefined || !api) {
      return;
    }
    api.forEachNode((node) => {
      if (node.data?.id === this.defaultRowId) {
        node.setSelected(true, true);
        api.ensureIndexVisible(node.rowIndex);
      }
    });
  }
}

const ENTITY_WITH_CACHE: PtEntity[] = [
  // ORGANIZZAZIONE
  'legalEntity',
  'organizationalFunctions',
  'userResources',
  'privacyRole',
  'employmentRelationship',
  // ASSETS
  'applications',
  'applicationTypes',

  // PROCESSI
  'regulations',
  'notices',
  'purposeCategory',
  'treatmentCategory',
  'recipients',
  'suppliers',

  // BUSINESS PROCESS MANAGEMENT
  'treatmentsHolder',
  'treatmentsResponsible',
  'treatmentsSubResponsible',
  'projectCards',
  'appAssessment',
  'globalMeasureCheck',
  'aiAssessment',
  'covidAssessment',

  // AMMINISTRAZIONE E CONFIGURAZIONE
  'organizationalMeasures',
  'organizationalMeasureCategories',
  'technicalMeasures',
  'technicalMeasureCategories',
  'preliminaryRiskAssessment',
  'interestedPersonCategories',
  'natureOfData',
  'damage',
  'legalBasis',
  'users',
];
